import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Gift, ArrowRight, ArrowLeft, RefreshCw, Sparkles, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import { api, resolveImage } from "@/lib/api";
import { swayStyleFor } from "@/lib/swayStyle";

/**
 * /gift-match  — programmatic-SEO relationship × occasion gift matcher.
 *
 * Every combo like /gift-match/husband/anniversary or /gift-match/best-friend/birthday
 * renders a unique landing page with tailored copy + a curated recommendation.
 * That means Google can index each combination separately for long-tail
 * queries like "gift for husband anniversary", "gift for best friend birthday",
 * etc. There are 10 relationships × 9 occasions = 90 unique landing pages,
 * all served from this one component.
 */

const RELATIONSHIPS = [
  { key: "husband",     label: "Your husband",       vibe: "grown" },
  { key: "wife",        label: "Your wife",          vibe: "sweet" },
  { key: "boyfriend",   label: "Your boyfriend",     vibe: "sexy" },
  { key: "girlfriend",  label: "Your girlfriend",    vibe: "sweet" },
  { key: "best-friend", label: "Your best friend",   vibe: "playful" },
  { key: "brother",     label: "Your brother",       vibe: "playful" },
  { key: "sister",      label: "Your sister",        vibe: "sweet" },
  { key: "dad",         label: "Your dad",           vibe: "grown" },
  { key: "coworker",    label: "A coworker",         vibe: "playful" },
  { key: "yourself",    label: "Yourself. Treat yourself.", vibe: "sexy" },
];

const OCCASIONS = [
  { key: "birthday",     label: "Birthday" },
  { key: "anniversary",  label: "Anniversary" },
  { key: "valentines",   label: "Valentine's Day" },
  { key: "fathers-day",  label: "Father's Day" },
  { key: "mothers-day",  label: "Mother's Day" },
  { key: "christmas",    label: "Christmas / Holidays" },
  { key: "just-because", label: "Just because" },
  { key: "housewarming", label: "Housewarming" },
  { key: "bachelorette", label: "Bachelorette / bachelor party" },
];

// Vibe → ranked product slugs
const VIBE_MAP = {
  sweet:    ["blush-and-bloom", "cherry-bomb", "freshly-baked"],
  sexy:     ["cherry-bomb", "sugared-skin", "deep-blue"],
  playful:  ["cherry-bomb", "blush-and-bloom", "freshly-baked"],
  cozy:     ["freshly-baked", "sugared-skin", "blush-and-bloom"],
  grown:    ["bare-wood", "deep-blue", "sugared-skin"],
};

// Personalised copy — the SEO magic. Each combo gets a bespoke pitch.
function pitchFor(relationship, occasion) {
  const r = relationship.label.replace("Your ", "").replace("Yourself. Treat yourself.", "yourself");
  const o = occasion.label.toLowerCase();
  const table = {
    "husband|anniversary": `${o.toUpperCase()} + ${r.toUpperCase()} = the "how did you know" gift.`,
    "wife|anniversary":     `Anniversary gift for your wife that's a little cheeky and a lot cured.`,
    "boyfriend|valentines": `Valentine's for your boyfriend — small package, huge reaction.`,
    "girlfriend|birthday":  `Birthday for your girlfriend. She'll laugh, then hang it in the car.`,
    "best-friend|birthday": `Best friend's birthday — the "you would" gift that becomes a running joke.`,
    "brother|christmas":    `Christmas for your brother — beats the socks-again gift.`,
    "sister|birthday":      `Birthday for your sister — cheeky enough she'll actually talk about it.`,
    "dad|fathers-day":      `Father's Day gift he'll pretend to hate and then absolutely use.`,
    "coworker|just-because": `Coworker gift that says "we're friends but I'm not weird".`,
    "yourself|just-because": `Treating yourself is a personality trait. Here's the merch.`,
  };
  const key = `${relationship.key}|${occasion.key}`;
  return table[key] || `The perfect ${o} gift for ${r === "yourself" ? "yourself" : r}. Small-batch. Adult-humor approved.`;
}

export default function GiftMatch() {
  const { relationship: rSlug, occasion: oSlug } = useParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);

  // Support two modes: quiz (no url slugs) OR direct landing (both slugs in URL).
  const relationship = RELATIONSHIPS.find((r) => r.key === rSlug) || null;
  const occasion = OCCASIONS.find((o) => o.key === oSlug) || null;
  const isResult = !!(relationship && occasion);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  // ---------- Quiz mode ----------
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({ relationship: null, occasion: null });

  const nextFromRelationship = (rk) => { setPicks({ ...picks, relationship: rk }); setStep(1); };
  const nextFromOccasion = (ok) => {
    setPicks({ ...picks, occasion: ok });
    nav(`/gift-match/${picks.relationship}/${ok}`);
  };

  // ---------- Result mode ----------
  const bundle = useMemo(() => {
    if (!isResult || products.length === 0) return [];
    const vibe = relationship.vibe || "sweet";
    const ranked = VIBE_MAP[vibe] || products.map((p) => p.id);
    // For big occasions push a 3-pack (buy 2 get 1 free hits at $18)
    const isBigOccasion = ["anniversary", "valentines", "christmas", "birthday"].includes(occasion.key);
    const count = isBigOccasion ? 3 : 2;
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    const chosen = [];
    for (const slug of ranked) {
      if (byId[slug] && byId[slug].active !== false) chosen.push(byId[slug]);
      if (chosen.length >= count) break;
    }
    // top up if needed
    for (const p of products) {
      if (chosen.length >= count) break;
      if (!chosen.find((c) => c.id === p.id)) chosen.push(p);
    }
    return chosen;
  }, [isResult, products, relationship, occasion]);

  const totalBase = useMemo(() => bundle.reduce((s, p) => s + (p.price || 0), 0), [bundle]);
  const isBogo = bundle.length >= 3;
  const total = isBogo ? totalBase - Math.min(...bundle.map((p) => p.price || 0)) : totalBase;

  // ---------- SEO ----------
  const path = isResult ? `/gift-match/${relationship.key}/${occasion.key}` : "/gift-match";
  const title = isResult
    ? `Gift for ${relationship.label.replace("Your ", "").replace("Yourself.", "you")} — ${occasion.label} · Booty Breeze`
    : "Gift Match Quiz — Find the Perfect Booty Breeze in 2 Clicks";
  const description = isResult
    ? `${pitchFor(relationship, occasion)} Hand-poured adult (18+) car air fresheners — ships free on 2+.`
    : "Answer two quick questions and we'll match a Booty Breeze scent to the person and the occasion. 90 unique gift ideas. Ships free on 2+.";
  const jsonLd = isResult ? [{
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": bundle.map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Product",
        "name": p.name,
        "url": `https://bootybreeze.com/product/${p.id}`,
        "image": (p.image_url || "").startsWith("http") ? p.image_url : undefined,
        "offers": { "@type": "Offer", "price": p.price, "priceCurrency": "USD" },
      },
    })),
  }] : undefined;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12" data-testid="gift-match-page">
      <SEO title={title} description={description} path={path} jsonLd={jsonLd} />

      {!isResult && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FF3D8B] to-[#A855F7] flex items-center justify-center">
            <Gift size={20} className="text-black" />
          </div>
          <div className="text-[#A855F7] uppercase text-xs tracking-widest">Gift Match Quiz · 2 questions</div>
        </div>
      )}

      {!isResult && (
        <>
          <h1 className="font-display text-4xl sm:text-6xl text-white mb-3">
            Find their <span className="text-neon-pink">perfect</span> Booty Breeze.
          </h1>
          <p className="text-white/60 mb-10 max-w-2xl">
            Two questions. One shameless recommendation. Ninety unique gift ideas already live for every relationship × occasion combo.
          </p>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="q1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <QuizStep
                  label="Question 1 of 2"
                  question="Who's the freshener for?"
                  options={RELATIONSHIPS}
                  onPick={(k) => nextFromRelationship(k)}
                  testIdPrefix="gift-match-relationship"
                />
              </motion.div>
            )}
            {step === 1 && (
              <motion.div key="q2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-4">
                  <button onClick={() => setStep(0)} className="text-white/50 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="gift-match-back">
                    <ArrowLeft size={13} /> Change answer
                  </button>
                </div>
                <QuizStep
                  label="Question 2 of 2"
                  question="What's the occasion?"
                  options={OCCASIONS}
                  onPick={(k) => nextFromOccasion(k)}
                  testIdPrefix="gift-match-occasion"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {isResult && (
        <>
          <Link to="/gift-match" className="text-white/50 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 mb-6" data-testid="gift-match-restart">
            <RefreshCw size={13} /> Restart quiz
          </Link>
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-2">
            {occasion.label} · {relationship.label}
          </div>
          <h1 className="font-display text-4xl sm:text-6xl text-white leading-[1.05] mb-4" data-testid="gift-match-title">
            {pitchFor(relationship, occasion)}
          </h1>
          <p className="text-white/60 max-w-2xl mb-10">
            Curated for the vibe. Small-batch, cured for 3–4 weeks of unapologetic longevity. Ships discreet from the USA.
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="gift-match-bundle">
            {bundle.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`gift-match-product-${p.id}`}
                className="group rounded-2xl border border-white/10 bg-[#0f0f0f] hover:border-[#FF3D8B] transition-colors overflow-hidden"
              >
                <div className="aspect-square p-4 overflow-visible flex items-center justify-center">
                  <img
                    src={resolveImage(p.image_url || p.image, 500, { removeBg: true })}
                    alt={p.name}
                    className="sway-hang w-full h-full object-contain drop-shadow-[0_15px_30px_rgba(255,61,139,0.35)]"
                    style={swayStyleFor(p.id)}
                  />
                </div>
                <div className="p-4 border-t border-white/5">
                  <div className="font-display text-lg text-white group-hover:text-[#FF3D8B] transition-colors">{p.name}</div>
                  <div className="text-white/50 text-xs">${p.price?.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4 rounded-2xl border border-[#FF3D8B]/40 bg-[#0f0f0f] p-6" data-testid="gift-match-cta">
            <div>
              <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">
                {isBogo ? "Buy 2 get 1 free — this bundle" : `${bundle.length}-piece gift set`}
              </div>
              <div className="font-display text-3xl text-white">
                ${total.toFixed(2)}
                {isBogo && (<span className="text-white/40 text-lg font-body ml-2 line-through">${totalBase.toFixed(2)}</span>)}
              </div>
              <div className="text-white/40 text-xs mt-1.5">Free shipping · ships discreet · gift-note optional at checkout</div>
            </div>
            <Link
              to="/shop"
              data-testid="gift-match-shop-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors"
            >
              <ShoppingBag size={15} /> Shop the set
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {OCCASIONS.filter((o) => o.key !== occasion.key).slice(0, 4).map((o) => (
              <Link
                key={o.key}
                to={`/gift-match/${relationship.key}/${o.key}`}
                data-testid={`gift-match-suggest-${o.key}`}
                className="rounded-xl border border-white/10 hover:border-[#A855F7] bg-[#0f0f0f] p-4 transition-colors flex items-center justify-between"
              >
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest mb-1">
                    Also for {relationship.label.replace("Your ", "").replace("Yourself. Treat yourself.", "yourself")}
                  </div>
                  <div className="text-white font-semibold">{o.label}</div>
                </div>
                <ArrowRight size={15} className="text-[#A855F7]" />
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function QuizStep({ label, question, options, onPick, testIdPrefix }) {
  return (
    <div>
      <div className="text-[#FF3D8B] uppercase text-[10px] tracking-widest mb-2">{label}</div>
      <h2 className="font-display text-3xl sm:text-4xl text-white mb-8">{question}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => onPick(o.key)}
            data-testid={`${testIdPrefix}-${o.key}`}
            className="group text-left rounded-xl border border-white/10 hover:border-[#FF3D8B] bg-[#0f0f0f] p-4 transition-colors flex items-center justify-between"
          >
            <span className="text-white font-medium">{o.label}</span>
            <ArrowRight size={16} className="text-white/40 group-hover:text-[#FF3D8B] transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}
